<template>
  <div class="knowledge-base-management">
    <ds-header title="知识库管理"></ds-header>

    <a-form layout="inline">
      <a-form-item label="名称：">
        <a-input
          v-model.trim="searchForm.name"
          placeholder="请输入知识库名称"
          allow-clear
          class="inputClass"
          @pressEnter="search"
        />
      </a-form-item>
      <a-form-item label="状态：">
        <a-select
          v-model="searchForm.status"
          placeholder="全部状态"
          style="width: 200px"
          :getPopupContainer="(triggerNode) => triggerNode.parentNode"
        >
          <a-select-option v-for="item in statusOptions" :key="item.value" :value="item.value">
            {{ item.label }}
          </a-select-option>
        </a-select>
      </a-form-item>
      <span>
        <a-button type="primary" @click="search" style="margin-top: 3px">
          <a-icon type="search" />查询
        </a-button>
        <a-button style="margin-left: 15px" @click="resetSearchForm">重置</a-button>
      </span>
    </a-form>

    <div class="toolbar">
      <a-button type="primary" @click="openAddModal">
        <a-icon type="plus" />新建知识库
      </a-button>
    </div>

    <a-table
      :rowKey="(record, index) => record.id || index"
      :columns="table.columns"
      :data-source="table.dataInfo"
      :pagination="table.pagination.total ? table.pagination : false"
      :loading="table.loading"
      :scroll="{ x: 1300 }"
      @change="pageChange"
      bordered
      style="margin-top: 15px"
    >
      <span slot="name" slot-scope="text, record">
        <div class="cell-main">{{ text }}</div>
        <div class="cell-sub" :title="record.description">{{ record.description || "暂无描述" }}</div>
      </span>
      <span slot="refCode" slot-scope="text">
        <a-tag v-if="text" color="blue" class="ref-code-tag">{{ text }}</a-tag>
        <span v-else>-</span>
      </span>
      <span slot="retrievalConfig" slot-scope="text, record">
        <div>TopK {{ formatNum(record.topK) }}</div>
        <div class="cell-sub num">阈值 {{ formatThreshold(record.similarityThreshold) }}</div>
      </span>
      <span slot="docCount" slot-scope="text, record">
        <a-button type="link" class="btnClass" @click="openDocView(record)">
          {{ getDocCount(record) != null ? `${getDocCount(record)} 篇` : "-" }}
        </a-button>
      </span>
      <span slot="status" slot-scope="text">
        <a-tag :color="getStatusConfig(text).color">
          {{ getStatusConfig(text).label }}
        </a-tag>
      </span>
      <span slot="createTime" slot-scope="text">
        <div class="time-cell">{{ formatDate(text) }}</div>
        <div class="time-cell time-sub">{{ formatTime(text) }}</div>
      </span>
      <span slot="action" slot-scope="text, record" class="action">
        <a-switch
          :checked="record.status === 0"
          @change="(checked) => handleStatusChange(record, checked)"
        />
        <a-button class="btnClass" type="link" @click="openEditModal(record)">编辑</a-button>
        <a-button class="btnClass" type="link" @click="openDocView(record)">文档管理</a-button>
        <a-button class="btnClass delete-btn" type="link" @click="handleDelete(record)">删除</a-button>
      </span>
    </a-table>

    <a-modal
      :maskClosable="false"
      :title="modalTitle"
      :visible="modalVisible"
      :confirm-loading="confirmLoading"
      width="720px"
      @ok="handleModalOk"
      @cancel="handleModalCancel"
    >
      <a-spin :spinning="modalLoading">
        <a-form-model
          ref="form"
          :model="form"
          :rules="rules"
          :label-col="{ span: 6 }"
          :wrapper-col="{ span: 16 }"
        >
          <a-form-model-item label="知识库名称" prop="name">
            <a-input
              v-model.trim="form.name"
              placeholder="请输入知识库名称"
              :maxLength="50"
              allow-clear
            />
          </a-form-model-item>

          <a-form-model-item :wrapper-col="{ span: 22 }">
            <biz-type-select ref="bizType" v-model="form.refCode"></biz-type-select>
          </a-form-model-item>

          <a-form-model-item label="知识库描述" prop="description">
            <a-textarea
              v-model="form.description"
              placeholder="简要说明知识库用途（选填）"
              :rows="2"
              :maxLength="200"
            />
          </a-form-model-item>

          <a-row>
            <a-col :span="12">
              <a-form-model-item
                label="TopK 召回条数"
                prop="topK"
                :label-col="{ span: 12 }"
                :wrapper-col="{ span: 12 }"
              >
                <a-input-number
                  v-model="form.topK"
                  :min="1"
                  :max="50"
                  :precision="0"
                  placeholder="1-50"
                  style="width: 100%"
                />
              </a-form-model-item>
            </a-col>
            <a-col :span="12">
              <a-form-model-item
                label="相似度阈值"
                prop="similarityThreshold"
                :label-col="{ span: 12 }"
                :wrapper-col="{ span: 12 }"
              >
                <a-input-number
                  v-model="form.similarityThreshold"
                  :min="0"
                  :max="1"
                  :step="0.01"
                  placeholder="0-1"
                  style="width: 100%"
                />
              </a-form-model-item>
            </a-col>
          </a-row>

          <a-form-model-item label="状态" prop="status">
            <a-select v-model="form.status" placeholder="请选择状态">
              <a-select-option v-for="item in formStatusOptions" :key="item.value" :value="item.value">
                {{ item.label }}
              </a-select-option>
            </a-select>
          </a-form-model-item>
        </a-form-model>
      </a-spin>
    </a-modal>

    <!-- 二级页面：文档管理（覆盖层，与一级页面同区域） -->
    <document-management
      v-if="docDrawerKnowledge"
      :knowledge="docDrawerKnowledge"
      @back="closeDocDrawer"
    ></document-management>
  </div>
</template>

<script>
import {
  getKnowledgePage,
  getKnowledgeDetail,
  createKnowledge,
  updateKnowledge,
  updateKnowledgeStatus,
  deleteKnowledge,
} from "@/api/intelligentCustomerService";
import {
  KNOWLEDGE_STATUS,
  KNOWLEDGE_STATUS_MAP,
  KNOWLEDGE_FORM_STATUS,
} from "../help";
import DocumentManagement from "./components/DocumentManagement.vue";
import BizTypeSelect from "./components/BizTypeSelect.vue";

// ===== 临时 Mock 数据（后端接口就绪后整段删除，含 USE_MOCK 开关与 applyMockList）=====
// 联调完成：接口已对齐，真实请求恢复；Mock 数据保留备查，确认稳定后整段删除
const USE_MOCK = false;

const MOCK_KB_LIST = [
  { id: 1, name: "园区企业服务知识库", refCode: "park-enterprise", description: "园区企业注册、税务、人才政策等常见问题解答", topK: 5, similarityThreshold: 0.72, status: 0, createTime: "2026-06-12 10:24:00", docCount: 12 },
  { id: 2, name: "商户经营指南", refCode: "merchant-guide", description: "商户入驻、结算规则、营销活动规范", topK: 8, similarityThreshold: 0.65, status: 0, createTime: "2026-06-20 15:41:00", docCount: 3 },
  { id: 3, name: "产品说明书库", refCode: "product-manual", description: "智能硬件产品使用说明与 FAQ", topK: 6, similarityThreshold: 0.8, status: 0, createTime: "2026-07-02 09:12:00", docCount: 2 },
  { id: 4, name: "内部制度文档", refCode: "internal-policy", description: "公司行政、人事、财务制度文件", topK: 4, similarityThreshold: 0.75, status: 1, createTime: "2026-07-18 14:03:00", docCount: 1 },
  { id: 5, name: "联调测试库", refCode: "test-kb", description: "开发联调用临时知识库", topK: 3, similarityThreshold: 0.5, status: 1, createTime: "2026-08-05 11:30:00", docCount: 2 },
];

const getDefaultForm = () => ({
  id: undefined,
  name: "",
  refCode: undefined,
  description: "",
  topK: 5,
  similarityThreshold: 0.7,
  status: 0,
});

export default {
  name: "knowledgeBaseManagement",
  components: { DocumentManagement, BizTypeSelect },
  data() {
    const validateTopK = (rule, value, callback) => {
      if (value === null || value === undefined || value === "") {
        return callback(new Error("请输入 TopK 召回条数"));
      }
      if (!Number.isInteger(value) || value < 1 || value > 50) {
        return callback(new Error("请输入 1-50 的整数"));
      }
      callback();
    };
    const validateThreshold = (rule, value, callback) => {
      if (value === null || value === undefined || value === "") {
        return callback(new Error("请输入相似度阈值"));
      }
      if (value < 0 || value > 1) {
        return callback(new Error("请输入 0 ~ 1 之间的数值"));
      }
      callback();
    };
    return {
      statusOptions: KNOWLEDGE_STATUS,
      formStatusOptions: KNOWLEDGE_FORM_STATUS,
      searchForm: {
        name: "",
        status: "",
      },
      // 非空即打开文档管理抽屉（值为知识库行数据副本）
      docDrawerKnowledge: null,
      mockNotified: false,
      table: {
        loading: false,
        columns: [
          {
            title: "ID",
            dataIndex: "id",
            key: "id",
            width: 90,
          },
          {
            title: "知识库名称",
            dataIndex: "name",
            key: "name",
            width: 280,
            scopedSlots: { customRender: "name" },
          },
          {
            title: "引用编码",
            dataIndex: "refCode",
            key: "refCode",
            width: 150,
            scopedSlots: { customRender: "refCode" },
          },
          {
            title: "检索配置",
            dataIndex: "topK",
            key: "retrievalConfig",
            width: 130,
            scopedSlots: { customRender: "retrievalConfig" },
          },
          {
            title: "文档数",
            dataIndex: "docCount",
            key: "docCount",
            width: 100,
            scopedSlots: { customRender: "docCount" },
          },
          {
            title: "状态",
            dataIndex: "status",
            key: "status",
            width: 100,
            scopedSlots: { customRender: "status" },
          },
          {
            title: "创建时间",
            dataIndex: "createTime",
            key: "createTime",
            width: 160,
            scopedSlots: { customRender: "createTime" },
          },
          {
            title: "操作",
            key: "action",
            width: 280,
            scopedSlots: { customRender: "action" },
          },
        ],
        dataInfo: [],
        pagination: {
          showSizeChanger: true,
          current: 1,
          pageSize: 10,
          total: 0,
          showQuickJumper: true,
          showTotal: (total) => `共 ${total} 条`,
        },
      },
      modalVisible: false,
      modalLoading: false,
      confirmLoading: false,
      modalType: "add",
      form: getDefaultForm(),
      rules: {
        name: [
          { required: true, message: "请输入知识库名称", trigger: "blur" },
          { max: 50, message: "知识库名称最多50个字符", trigger: "blur" },
        ],
        description: [{ max: 200, message: "知识库描述最多200个字符", trigger: "blur" }],
        topK: [{ required: true, validator: validateTopK, trigger: "change" }],
        similarityThreshold: [
          { required: true, validator: validateThreshold, trigger: "change" },
        ],
        status: [{ required: true, message: "请选择状态", trigger: "change" }],
      },
    };
  },
  computed: {
    modalTitle() {
      return this.modalType === "add" ? "新建知识库" : "编辑知识库";
    },
  },
  mounted() {
    this.fetchList();
  },
  methods: {
    isSuccess(res) {
      return res && [0, "0", 200, "200"].includes(res.code);
    },
    getStatusConfig(status) {
      return KNOWLEDGE_STATUS_MAP[status] || { label: "-", color: "default" };
    },
    formatThreshold(value) {
      if (value === null || value === undefined || value === "") return "-";
      return Number(value).toFixed(2);
    },
    formatNum(value) {
      if (value === null || value === undefined || value === "") return "-";
      return Number(value).toLocaleString("zh-CN");
    },
    getDocCount(record) {
      // 分页接口暂不返回文档数，无值时表格显示 "-"，进文档管理后头部可见真实数量
      return record.docCount ?? record.documentCount ?? null;
    },
    formatDate(time) {
      if (!time) return "-";
      return String(time).split(" ")[0] || time;
    },
    formatTime(time) {
      if (!time) return "";
      const parts = String(time).split(" ");
      return parts[1] || "";
    },
    buildQueryParams() {
      const params = {
        pageNo: this.table.pagination.current,
        pageSize: this.table.pagination.pageSize,
      };
      if (this.searchForm.name) {
        params.name = this.searchForm.name;
      }
      if (this.searchForm.status !== "" && this.searchForm.status !== undefined) {
        params.status = this.searchForm.status;
      }
      return params;
    },
    search() {
      this.table.pagination.current = 1;
      this.fetchList();
    },
    resetSearchForm() {
      this.table.pagination.current = 1;
      this.table.pagination.pageSize = 10;
      this.searchForm = this.$options.data.call(this).searchForm;
      this.fetchList();
    },
    pageChange(option) {
      this.table.pagination.current = option.current;
      this.table.pagination.pageSize = option.pageSize;
      this.fetchList();
    },
    async fetchList() {
      if (USE_MOCK) {
        this.applyMockList();
        return;
      }
      this.table.loading = true;
      try {
        const res = await getKnowledgePage(this.buildQueryParams());
        if (this.isSuccess(res)) {
          if (Array.isArray(res.data)) {
            this.table.dataInfo = res.data;
            this.table.pagination.total = res.data.length;
          } else {
            this.table.dataInfo = res.data?.list || res.data?.records || [];
            this.table.pagination.total = res.data?.total || 0;
          }
        } else {
          this.$message.error(res?.msg || "获取列表失败");
        }
      } catch {
        this.$message.error("获取列表失败");
      } finally {
        this.table.loading = false;
      }
    },
    // 临时 Mock：预览样式用，联调后删除本方法及调用处
    applyMockList() {
      const name = (this.searchForm.name || "").trim();
      const status = this.searchForm.status;
      const filtered = MOCK_KB_LIST.filter(
        (item) =>
          (!name || item.name.includes(name)) &&
          (status === "" || status === undefined || item.status === Number(status))
      );
      const { current, pageSize } = this.table.pagination;
      this.table.dataInfo = filtered.slice((current - 1) * pageSize, current * pageSize);
      this.table.pagination.total = filtered.length;
      if (!this.mockNotified) {
        this.mockNotified = true;
        this.$message.warning("预览模式：当前展示 Mock 数据");
      }
    },
    resetForm() {
      this.form = getDefaultForm();
      // 业务类型组件内部状态需显式复位（value 未变化时 watch 不触发）
      if (this.$refs.bizType) {
        this.$refs.bizType.reset();
      }
      this.$nextTick(() => {
        if (this.$refs.form) {
          this.$refs.form.clearValidate();
        }
      });
    },
    openAddModal() {
      this.modalType = "add";
      this.resetForm();
      this.modalVisible = true;
    },
    fillFormFromDetail(data) {
      this.form = {
        id: data.id,
        name: data.name || "",
        refCode: data.refCode || undefined,
        description: data.description || "",
        topK: data.topK != null ? data.topK : 5,
        similarityThreshold: data.similarityThreshold != null ? data.similarityThreshold : 0.7,
        status: data.status != null ? data.status : 0,
      };
    },
    async openEditModal(record) {
      this.modalType = "edit";
      this.resetForm();
      this.modalVisible = true;
      this.modalLoading = true;
      try {
        const res = await getKnowledgeDetail(record.id);
        if (this.isSuccess(res) && res.data) {
          this.fillFormFromDetail(res.data);
        } else {
          this.$message.error(res?.msg || "获取详情失败");
          this.modalVisible = false;
        }
      } catch {
        this.$message.error("获取详情失败");
        this.modalVisible = false;
      } finally {
        this.modalLoading = false;
      }
    },
    handleModalCancel() {
      this.modalVisible = false;
      this.resetForm();
    },
    buildSavePayload() {
      const payload = {
        name: this.form.name,
        refCode: this.form.refCode,
        // 空描述一并提交，保证编辑时可以清空
        description: this.form.description,
        topK: this.form.topK,
        similarityThreshold: this.form.similarityThreshold,
        status: this.form.status,
      };
      if (this.modalType === "edit") {
        payload.id = this.form.id;
      }
      return payload;
    },
    handleModalOk() {
      this.$refs.form.validate(async (valid) => {
        // 业务类型两段式校验由组件内部表单项渲染行内错误
        const bizValid = this.$refs.bizType ? await this.$refs.bizType.validate() : true;
        if (!valid || !bizValid) return;
        this.confirmLoading = true;
        try {
          const isEdit = this.modalType === "edit";
          const api = isEdit ? updateKnowledge : createKnowledge;
          const res = await api(this.buildSavePayload());
          if (this.isSuccess(res)) {
            this.$message.success(res.msg || "保存成功");
            this.handleModalCancel();
            if (isEdit) {
              this.fetchList();
            } else {
              this.search();
            }
          } else {
            this.$message.error(res?.msg || "保存失败");
          }
        } catch {
          this.$message.error("保存失败");
        } finally {
          this.confirmLoading = false;
        }
      });
    },
    handleStatusChange(record, checked) {
      const status = checked ? 0 : 1;
      const actionText = checked ? "启用" : "停用";
      const that = this;
      this.$confirm({
        title: `确认${actionText}知识库「${record.name}」？`,
        content: checked ? "" : "停用后该知识库将不参与检索。",
        onOk() {
          return updateKnowledgeStatus({ id: record.id, status })
            .then((res) => {
              if (that.isSuccess(res)) {
                that.$message.success(res.msg || `${actionText}成功`);
                that.fetchList();
              } else {
                that.$message.error(res?.msg || `${actionText}失败`);
              }
            })
            .catch(() => {
              that.$message.error(`${actionText}失败`);
            });
        },
      });
    },
    handleDelete(record) {
      const docCount = this.getDocCount(record);
      const that = this;
      this.$confirm({
        title: `确认删除知识库「${record.name}」？`,
        content:
          docCount > 0
            ? `该库下还有 ${docCount} 篇文档，将一并删除；删除后不可恢复。`
            : "库内文档将一并删除，且删除后不可恢复。",
        okType: "danger",
        onOk() {
          return deleteKnowledge(record.id)
            .then((res) => {
              if (that.isSuccess(res)) {
                that.$message.success(res.msg || "删除成功");
                // 当前页删到最后一条时回退一页，避免停留在空页
                if (that.table.dataInfo.length === 1 && that.table.pagination.current > 1) {
                  that.table.pagination.current -= 1;
                }
                that.fetchList();
              } else {
                that.$message.error(res?.msg || "删除失败");
              }
            })
            .catch(() => {
              that.$message.error("删除失败");
            });
        },
      });
    },
    // ---- 以下入口对应的下级页面/抽屉在后续迭代中接入 ----
    openDocView(record) {
      this.docDrawerKnowledge = { ...record };
    },
    closeDocDrawer() {
      this.docDrawerKnowledge = null;
      // 返回时刷新知识库列表，同步文档数
      this.fetchList();
    },
  },
};
</script>

<style scoped lang="scss">
.knowledge-base-management {
  // 操作列吸右：用原生 sticky 替代 antd fixed 右列——fixed 列是独立表格图层，
  // 多行单元格下会行高错位、竖边框叠成双线；sticky 单表格渲染无此问题
  ::v-deep .ant-table {
    th:last-child,
    td:last-child {
      position: sticky;
      right: 0;
      z-index: 1;
      background: #fff;
      // 吸右覆盖左侧内容时，分隔线由本列左边框绘制
      border-left: 1px solid #e8e8e8;
    }

    // 分隔线统一由最后一列绘制，去掉倒数第二列的右边框避免双线
    th:nth-last-child(2),
    td:nth-last-child(2) {
      border-right: none;
    }

    th:last-child {
      z-index: 2;
      background: #fafafa;
    }

    tbody tr:hover td:last-child {
      background: #e6f7ff;
    }
  }

  .toolbar {
    display: flex;
    align-items: center;
    padding-top: 15px;
  }

  .cell-main {
    font-weight: 600;
    font-size: 13.5px;
  }

  .cell-sub {
    font-size: 12px;
    color: rgba(0, 0, 0, 0.45);
    margin-top: 3px;
    max-width: 320px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .num {
    font-variant-numeric: tabular-nums;
  }

  .time-cell {
    line-height: 1.4;
  }

  .time-sub {
    color: rgba(0, 0, 0, 0.45);
    font-size: 12px;
  }

  .action {
    display: flex;
    align-items: center;
    gap: 8px;

    // 清掉 antd 链接按钮默认 15px 内边距，避免操作内容超出列宽压到左侧列
    ::v-deep .ant-btn {
      padding: 0;
    }
  }

  .delete-btn {
    color: #ff4d4f;
  }

  .ref-code-tag {
    font-family: monospace;
  }
}
</style>
